import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cdsur_token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check token validity on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Token verification failed:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('cdsur_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (name, email, password, role = 'lectura') => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      role
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('cdsur_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('cdsur_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const isAdmin = user?.role === 'admin';
  // Usuario restringido a la herramienta /load-docs (sin admin ni quickactions)
  const isLoader = user?.role === 'loader';
  const adminTools = user?.admin_tools || [];
  const areas = user?.areas || [];
  // Super admin tiene acceso a todas las herramientas; el resto solo a las asignadas.
  const hasAdminTool = (key) => isAdmin || adminTools.includes(key);
  // Permiso para usar las acciones de ELIMINAR de una herramienta (el check
  // "permite eliminar" de Administrar Usuarios). Espeja a `auth.require_tool_delete`
  // del backend: acá solo se decide qué se muestra, la autorización real la hace
  // el endpoint.
  const toolsDelete = user?.admin_tools_delete || [];
  const canDeleteIn = (key) => isAdmin || (adminTools.includes(key) && toolsDelete.includes(key));
  // Puede entrar al panel admin si es super admin o tiene al menos una herramienta.
  const canAccessAdmin = isAdmin || adminTools.length > 0;
  // Carga de Documentos: el admin y el loader siempre; un "user" solo si le
  // asignaron la herramienta. Espeja a `auth.require_loader` del backend.
  const canLoadDocs = isAdmin || isLoader || adminTools.includes('load-docs');
  // Saldo FIMA dentro de Carga de Documentos: el admin siempre; un "user" solo
  // con la herramienta y el check "FIMA"; el loader nunca. Espeja a
  // `auth.puede_cargar_fima` del backend (que es el que autoriza de verdad).
  // No se compara contra role === 'user': /auth/me devuelve el rol tal cual está
  // en la base y hay usuarios con roles legacy (lectura, operaciones...) que el
  // backend trata como "user".
  const canLoadFima = isAdmin || (
    !isLoader && adminTools.includes('load-docs') && !!user?.load_docs_fima
  );
  // Listados de cheques del banco: mismo criterio, permiso propio. Espeja a
  // `auth.puede_cargar_cheques`. Son dos permisos separados a propósito: el
  // saldo del fondo y la cartera de cheques los mira gente distinta.
  const canLoadCheques = isAdmin || (
    !isLoader && adminTools.includes('load-docs') && !!user?.load_docs_cheques
  );

  // Panel de quickactions: el backend filtra las acciones por área
  // (routes/ai_config.py), así que sin áreas asignadas el chat queda vacío y no
  // cuenta como panel disponible.
  const canAccessChat = isAdmin || (!isLoader && areas.length > 0);

  // Paneles (entornos de trabajo completos) a los que llega este usuario.
  // Fuente única para: la pantalla de inicio, la resolución del landing y la
  // visibilidad del botón "Volver al inicio".
  const paneles = [];
  if (canAccessChat) {
    paneles.push({
      key: 'diagnostico',
      path: '/diagnostico',
      label: 'Diagnóstico Operativo',
      description: 'Quick actions y consultas sobre los datos del ERP',
    });
  }
  if (canAccessAdmin) {
    paneles.push({
      key: 'admin',
      path: '/admin',
      label: 'Administración',
      description: 'Herramientas de gestión y procesos automáticos',
    });
  }

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isAdmin,
    isLoader,
    areas,
    adminTools,
    toolsDelete,
    hasAdminTool,
    canDeleteIn,
    canAccessAdmin,
    canLoadDocs,
    canLoadFima,
    canLoadCheques,
    canAccessChat,
    paneles,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
